import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ReportDashboard from './components/ReportDashboard';
import ClientReportPage from './pages/ClientReportPage';
import ClientReportsListPage from './pages/ClientReportsListPage';
import ClientPortalPublic from './pages/ClientPortalPublicV2';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import TikTokOAuthCallback from './pages/TikTokOAuthCallback';

// Create a client for React Query
const queryClient = new QueryClient();

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:campaignId"
              element={
                <ProtectedRoute>
                  <ReportDashboard />
                </ProtectedRoute>
              }
            />
            {/* Client Reports */}
            <Route
              path="/client-reports/:campaignId"
              element={
                <ProtectedRoute>
                  <ClientReportsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client-report/:campaignId/new"
              element={
                <ProtectedRoute>
                  <ClientReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client-report/:campaignId/:reportId"
              element={
                <ProtectedRoute>
                  <ClientReportPage />
                </ProtectedRoute>
              }
            />
            {/* Client Portal - Public (no auth required) */}
            <Route path="/client-portal/:accessToken" element={<ClientPortalPublic />} />
            {/* Legal Pages - Public */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            {/* TikTok OAuth bridge — receives ?code & ?state from TikTok and forwards to backend */}
            <Route
              path="/oauth/tiktok/callback"
              element={
                <ProtectedRoute>
                  <TikTokOAuthCallback />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
